import { Component, ChangeDetectionStrategy, Input, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { filter } from 'rxjs/operators';

interface Crumb {
  label: string;
  url: string;
}

@Component({
  selector: 'app-breadcrumbs',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule],
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbsComponent {
  private _lastLabel: string | null = null;

  @Input() set lastLabel(value: string | null | undefined) {
    this._lastLabel = value ?? null;
    this.refresh();
  }
  get lastLabel(): string | null { 
    return this._lastLabel; 
  }

  crumbs: Crumb[] = [];

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.refresh());

    this.refresh();
  }

  private refresh(): void {
    const built = this.build(this.route.root);

    if (this._lastLabel && built.length) {
      built[built.length - 1] = { ...built[built.length - 1], label: this._lastLabel };
    }

    this.crumbs = built;
    this.cdr.markForCheck();
  }

  private build(route: ActivatedRoute, url: string = '', acc: Crumb[] = []): Crumb[] {
    const rc = route.routeConfig;
    const label = rc?.data?.['breadcrumb'] as string | undefined;
    const parent = rc?.data?.['parent'] as { label: string; url: string } | undefined;

    const segment = route.snapshot.url.map(s => s.path).join('/');
    const nextUrl = segment ? `${url}/${segment}` : url;

    if (parent && !acc.some(c => c.url === parent.url)) {
      acc.push({ label: parent.label, url: parent.url });
    }
    if (label) {
      acc.push({ label, url: nextUrl || '/' });
    }

    const child = route.firstChild;
    return child ? this.build(child, nextUrl, acc) : acc;
  }
}